import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import type {
  EditTripSegment,
  EditTripStep,
} from '@apps/main/new-desktop/modules/editTripModal';
import { EditTripStepType } from '@apps/main/new-desktop/modules/editTripModal';
import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
import { MapStateKey, RecipeStateKey } from '@apps/main/modules/state/state';
import { isStayIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';

import {
  getEditTripSegmentFromHotelIngredient,
  getNewRecipeOrder,
  updateRecipeTransportPorts,
} from './editTripStoreLogic';

export const useEditTripStore = defineStore('EditTripStore', () => {
  const recipeState = injectStrict(RecipeStateKey);
  const mapState = injectStrict(MapStateKey);

  // State
  const DEFAULT_INITIAL_STEP: EditTripStep = {
    stepType: EditTripStepType.Overview,
    isInitialStep: true,
  };

  const initialStep = ref<EditTripStep>(DEFAULT_INITIAL_STEP);

  // Actions
  function resetInitialStep(): void {
    initialStep.value = DEFAULT_INITIAL_STEP;
  }

  function changeTripOrder(segments: EditTripSegment[]): void {
    const newRecipe = getNewRecipeOrder(segments, recipeState.recipe.value);

    recipeState.recipe.value = updateRecipeTransportPorts(
      newRecipe,
      mapState.startingLocation.value?.position
    );
  }

  function changeNumberOfNights(segment: EditTripSegment): void {
    const stayIngredient = recipeState.getStayIngredient(segment.ingredientId);
    if (!stayIngredient) {
      return;
    }
    stayIngredient.numberOfNights = segment.numberOfNights;
  }

  // Getters
  const modalData = computed<EditTripSegment[]>((): EditTripSegment[] =>
    recipeState.recipe.value
      .filter(isStayIngredient)
      .map((ingredient) =>
        getEditTripSegmentFromHotelIngredient(
          ingredient,
          mapState.calculatedRoute.value?.[
            recipeState.recipe.value.indexOf(ingredient)
          ]
        )
      )
  );

  const hasBookableTransport = computed<boolean>(
    (): boolean =>
      !!recipeState.flightOnlyRecipe.value.length ||
      !!recipeState.ferryOnlyRecipe.value.length
  );

  return {
    // State
    initialStep,

    // Actions
    resetInitialStep,
    changeTripOrder,
    changeNumberOfNights,

    // Getters
    hasBookableTransport,
    modalData,
  };
});
